(() => {
  const buybarSelector = '[data-its-mobile-buybar]';

  const collectMatches = (scope, selector) => {
    const matches = [];
    if (scope?.matches?.(selector)) matches.push(scope);
    if (scope?.querySelectorAll) matches.push(...scope.querySelectorAll(selector));
    return matches;
  };

  const bindCustomProperties = (scope) => {
    collectMatches(scope, 'product-info').forEach((productInfo) => {
      const sectionId = productInfo.dataset.section;
      const form =
        document.getElementById(`product-form-${sectionId}`) ||
        productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form?.id) return;

      productInfo.querySelectorAll('input[name^="properties["]').forEach((input) => {
        if (!input.closest('form')) input.setAttribute('form', form.id);
      });

      productInfo.querySelectorAll('[class*="cover-selector-wrapper-"]').forEach((wrapper, index) => {
        const label = wrapper.querySelector('[class*="cover-selector-label-"]');
        const options = wrapper.querySelector('[class*="cover-options-"]');
        if (!label || !options) return;

        if (!label.id) label.id = `CoverSelectorLabel-${sectionId}-${index}`;
        options.setAttribute('role', 'radiogroup');
        options.setAttribute('aria-labelledby', label.id);
      });
    });
  };

  const getSection = (bar) => bar.closest('[data-section]') || bar.parentElement;

  const getMainSubmitButton = (bar) => {
    const section = getSection(bar);
    const buttonById = document.getElementById(`ProductSubmitButton-${bar.dataset.sectionId}`);

    if (buttonById && section?.contains(buttonById)) return buttonById;
    return section?.querySelector('product-info .product-form__submit') || null;
  };

  const getButtonLabel = (button) => {
    const label = button?.querySelector('span')?.textContent || button?.textContent;
    return label?.trim() || '';
  };

  const syncBuybarState = (bar) => {
    const button = bar.querySelector('[data-its-mobile-buybar-submit]');
    if (!button) return;

    const mainButton = getMainSubmitButton(bar);
    const targetForm = button.form;
    const targetsMainForm = Boolean(mainButton?.form && targetForm && mainButton.form === targetForm);
    const disabled =
      !mainButton ||
      !targetsMainForm ||
      mainButton.disabled ||
      mainButton.getAttribute('aria-disabled') === 'true';
    const label =
      getButtonLabel(mainButton) ||
      (disabled ? bar.dataset.soldOutLabel : bar.dataset.availableLabel) ||
      button.textContent.trim();

    button.disabled = disabled;
    button.toggleAttribute('aria-disabled', disabled);
    if (button.textContent.trim() !== label) button.textContent = label;
  };

  const bindVisibilityObserver = (bar) => {
    if (!('IntersectionObserver' in window)) return;

    const section = getSection(bar);
    const trigger = section?.querySelector('.product-form__submit') || section?.querySelector('[data-its-product-intro]');
    if (trigger === bar.itsMobileBuybarTrigger) return;

    bar.itsMobileBuybarObserver?.disconnect();
    bar.itsMobileBuybarTrigger = trigger || null;
    if (!trigger) {
      bar.hidden = true;
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        bar.hidden = entry.boundingClientRect.bottom >= 0;
      },
      { threshold: 0 }
    );

    observer.observe(trigger);
    bar.itsMobileBuybarObserver = observer;
  };

  const scheduleSync = (bar) => {
    if (!bar.isConnected || bar.itsMobileBuybarSyncFrame) return;

    bar.itsMobileBuybarSyncFrame = requestAnimationFrame(() => {
      bar.itsMobileBuybarSyncFrame = null;
      bindVisibilityObserver(bar);
      syncBuybarState(bar);
    });
  };

  const mutationAffectsMainForm = (mutation, bar) => {
    const target = mutation.target.nodeType === Node.TEXT_NODE ? mutation.target.parentElement : mutation.target;
    if (!target || target === bar || bar.contains(target)) return false;
    if (target.closest?.('product-info, .product-form__submit')) return true;

    return [...mutation.addedNodes, ...mutation.removedNodes].some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.('product-info, .product-form__submit') ||
          node.querySelector?.('product-info, .product-form__submit'))
    );
  };

  const destroyBuybar = (bar) => {
    bar.itsMobileBuybarObserver?.disconnect();
    bar.itsMobileBuybarStateObserver?.disconnect();
    bar.itsMobileBuybarUnsubscribe?.();
    if (bar.itsMobileBuybarSyncFrame) cancelAnimationFrame(bar.itsMobileBuybarSyncFrame);

    bar.itsMobileBuybarObserver = null;
    bar.itsMobileBuybarStateObserver = null;
    bar.itsMobileBuybarUnsubscribe = null;
    bar.itsMobileBuybarSyncFrame = null;
    bar.itsMobileBuybarTrigger = null;
    delete bar.dataset.itsMobileBuybarReady;
  };

  const init = (scope = document) => {
    bindCustomProperties(scope);

    collectMatches(scope, buybarSelector).forEach((bar) => {
      if (bar.dataset.itsMobileBuybarReady === 'true') {
        scheduleSync(bar);
        return;
      }
      bar.dataset.itsMobileBuybarReady = 'true';

      const section = getSection(bar);
      if (section && 'MutationObserver' in window) {
        const observer = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => mutationAffectsMainForm(mutation, bar))) scheduleSync(bar);
        });
        observer.observe(section, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          characterData: true,
          childList: true,
          subtree: true,
        });
        bar.itsMobileBuybarStateObserver = observer;
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        bar.itsMobileBuybarUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== bar.dataset.sectionId) return;
          scheduleSync(bar);
        });
      }

      bindVisibilityObserver(bar);
      syncBuybarState(bar);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    collectMatches(event.target, buybarSelector).forEach(destroyBuybar);
  });

  if ('MutationObserver' in window) {
    const lifecycleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) collectMatches(node, buybarSelector).forEach(destroyBuybar);
        });
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(`product-info, ${buybarSelector}`) || node.querySelector?.(`product-info, ${buybarSelector}`))
          ) {
            init(node);
          }
        });
      });
    });

    lifecycleObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
